import React, { useMemo, useState } from 'react';
import type { AppState, RolPermiso } from '../types';
import { PageShell, glassLight, primaryButton, ghostButton, dangerButton, Badge, EmptyState, useIsMobile, usePagination, Pagination, T, Modal } from '../ui';
// Colores fijos para los primeros roles; el resto usa un color genérico
const ROL_COLORS = [{
  color: '#8b5cf6',
  soft: 'rgba(139,92,246,0.14)'
}, {
  color: T.info,
  soft: T.infoSoft
}, {
  color: T.accent,
  soft: T.accentSoft
}, {
  color: '#ec4899',
  soft: 'rgba(236,72,153,0.14)'
}, {
  color: '#10b981',
  soft: 'rgba(16,185,129,0.14)'
}];
interface Props {
  state: AppState;
  onNew: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  canArchive: boolean;
}
export function Usuarios({
  state,
  onNew,
  onEdit,
  onDelete,
  canArchive
}: Props) {
  const [tab, setTab] = useState<'activos' | 'archivados'>('activos');
  const [confirm, setConfirm] = useState<string | null>(null);
  const isMobile = useIsMobile();
  // Construir etiquetas y colores dinámicamente desde los roles reales
  const rolLabel: Record<string, string> = {};
  const rolColor: Record<string, {
    color: string;
    soft: string;
  }> = {};
  state.roles.forEach((r, i) => {
    const key = r.id.replace(/^rol-/, '');
    rolLabel[key] = r.nombre;
    rolColor[key] = ROL_COLORS[i % ROL_COLORS.length];
  });
  const filteredUsuarios = useMemo(() => {
    return state.usuarios.filter(u => tab === 'activos' ? !u.archivado : u.archivado);
  }, [state.usuarios, tab]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filteredUsuarios, 8);
  const sucNamesFor = (ids?: string[]) => {
    const safeIds = Array.isArray(ids) ? ids : [];
    return safeIds.length === 0 ? <span style={{
      color: '#c4b5fd',
      fontStyle: 'italic'
    }} data-id="element-1018" data-mp="123ifedpdopy">
      
        Todas las sucursales
      </span> : safeIds.map(sid => state.sucs.find(x => x.id === sid)?.nombre ?? sid).join(', ');
  };
  return <PageShell title="Usuarios y acceso" sub="Gestiona quién puede entrar y qué sucursales puede ver" action={<button onClick={onNew} style={primaryButton} data-id="element-1020" data-mp="ark9i7clzc8a">
          + Nuevo usuario
        </button>} data-id="element-1019" data-mp="foaoercog1s4">
      
      <div style={{
      display: 'flex',
      gap: 8,
      marginBottom: 20
    }} data-id="element-1021" data-mp="5puz8tx7n2l9">
        
        <button onClick={() => {
        setTab('activos');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'activos' ? T.accentSoft : 'transparent',
        color: tab === 'activos' ? T.accent : T.textMuted
      }} data-id="element-1022" data-mp="r9xgq2b8aup">
          
          Activos
        </button>
        {canArchive && <button onClick={() => {
        setTab('archivados');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'archivados' ? T.accentSoft : 'transparent',
        color: tab === 'archivados' ? T.accent : T.textMuted
      }} data-id="element-1023" data-mp="3j3y8lcpsn2kr">
          
            Archivados
          </button>}
      </div>

      {filteredUsuarios.length === 0 ? <EmptyState title="Sin usuarios registrados" hint="Agrega usuarios y define a qué sucursales tienen acceso." icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" data-id="element-1025" data-mp="1oo9bbr5ukztx">
          
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" data-id="element-1026" data-mp="1yo9yxpar223x" />
              <circle cx="9" cy="7" r="4" data-id="element-1027" data-mp="1109m4vo6phhg" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" data-id="element-1028" data-mp="111azx7wl4i2v" />
            </svg>} data-id="element-1024" data-mp="1kn7bp980qlrf" /> : isMobile ? <>
          <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }} data-id="element-1029" data-mp="15ykrat3jb3tc">
          
            {pageItems.map(u => {
          const rc = rolColor[u.rol] ?? {
            color: T.textMuted,
            soft: 'rgba(255,255,255,0.06)'
          };
          return <div key={u.id} style={{
            ...glassLight,
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 12
          }} data-id="element-1030" data-mp="ia9wo7ofm0di">
                
                  <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 10
            }} data-id="element-1031" data-mp="2oz28lhlwb0rb">
                  
                    <div style={{
                minWidth: 0
              }} data-id="element-1032" data-mp="2yovga7ix2vqa">
                    
                      <div style={{
                  fontWeight: 700,
                  color: T.text,
                  fontSize: 15
                }} data-id="element-1033" data-mp="2twbuwodzmwax">
                      
                        {u.nombre}
                      </div>
                      <div style={{
                  fontSize: 13,
                  color: T.textSecondary,
                  marginTop: 2,
                  wordBreak: 'break-all'
                }} data-id="element-1034" data-mp="33p9fr16gcxcp">
                      
                        {u.correo}
                      </div>
                    </div>
                    <div style={{
                flexShrink: 0
              }} data-id="element-1035" data-mp="1i9lo4nzwgs4b">
                    
                      <Badge label={rolLabel[u.rol]} color={rc.color} soft={rc.soft} data-id="element-1036" data-mp="3tx7a1gdfz1dj" />
                    
                    </div>
                  </div>
                  <div data-id="element-1037" data-mp="2puq4tl6c9tx">
                    <div style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: T.textMuted,
                marginBottom: 4
              }} data-id="element-1038" data-mp="2sgw4osf2ha1">
                    
                      Sucursales asignadas
                    </div>
                    <div style={{
                fontSize: 13,
                color: T.textSecondary,
                lineHeight: 1.45
              }} data-id="element-1039" data-mp="3g6bu5k4izk0g">
                    
                      {sucNamesFor(u.sucursalesIds)}
                    </div>
                  </div>
                  <div style={{
              display: 'flex',
              gap: 8,
              paddingTop: 4,
              borderTop: `1px solid ${T.border}`
            }} data-id="element-1040" data-mp="10e4sj9ovgzlk">
                  
                    <button className="getr-btn-ghost" onClick={() => onEdit(u.id)} style={{
                ...ghostButton,
                flex: 1,
                marginTop: 8
              }} data-id="element-1041" data-mp="2dd6ohnvfxk07">
                    
                      Editar
                    </button>
                    {u.rol !== 'admin' && <button onClick={() => setConfirm(u.id)} style={{
                ...ghostButton,
                flex: 1,
                marginTop: 8,
                color: u.archivado ? T.info : T.warn,
                borderColor: u.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
                background: u.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
              }} data-id="element-1042" data-mp="1zdx9griz0vxt">
                    
                        {u.archivado ? 'Desarchivar' : 'Archivar'}
                      </button>}
                  </div>
                </div>;
        })}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="usuarios" data-id="element-1043" data-mp="3b0f3r14ulwtr" />
        
        </> : <div style={{
      ...glassLight,
      overflow: 'hidden'
    }} data-id="element-1044" data-mp="3nawo3uuc1oy3">
        
          <div style={{
        overflowX: 'auto'
      }} data-id="element-1045" data-mp="3nd0rmbobklxu">
          
            <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13
        }} data-id="element-1046" data-mp="3s8g16rfanvog">
            
              <thead data-id="element-1047" data-mp="55baae0ip68v">
                <tr style={{
              background: T.surfaceSubtle
            }} data-id="element-1048" data-mp="55th24kt32v1">
                
                  {['Usuario', 'Correo', 'Rol', 'Sucursales asignadas', 'Acciones'].map(h => <th key={h} style={{
                padding: '13px 16px',
                fontWeight: 700,
                fontSize: 10,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: T.textMuted,
                textAlign: 'left',
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-1049" data-mp="ev4ir65d558w">
                  
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-1050" data-mp="1e7xd085xw7hd">
                {pageItems.map((u, i) => {
              const uSucIds = Array.isArray(u.sucursalesIds) ? u.sucursalesIds : [];
              const sucNames = uSucIds.length === 0 ? <span style={{
                color: '#c4b5fd',
                fontStyle: 'italic'
              }} data-id="element-1051" data-mp="1o38rqqhclep4">
                  
                        Todas las sucursales
                      </span> : uSucIds.map(sid => {
                const s = state.sucs.find(x => x.id === sid);
                return s ? s.nombre : sid;
              }).join(', ');
              const rc = rolColor[u.rol] ?? {
                color: T.textMuted,
                soft: 'rgba(255,255,255,0.06)'
              };
              return <tr key={u.id} className="getr-row" style={{
                background: i % 2 === 1 ? T.surface : 'transparent',
                borderBottom: i < pageItems.length - 1 ? `1px solid ${T.border}` : 'none',
                transition: 'background .15s'
              }} data-id="element-1052" data-mp="10cl6l3if8skm" data-mpi={i}>
                    
                      <td style={{
                  padding: '13px 16px',
                  fontWeight: 600,
                  color: T.text
                }} data-id="element-1053" data-mp="2o3u7535yfy8u">
                      
                        {u.nombre}
                      </td>
                      <td style={{
                  padding: '13px 16px',
                  color: T.textSecondary
                }} data-id="element-1054" data-mp="2eesr5czetguv">
                      
                        {u.correo}
                      </td>
                      <td style={{
                  padding: '13px 16px'
                }} data-id="element-1055" data-mp="371wwxk6btz7p">
                      
                        <Badge label={rolLabel[u.rol]} color={rc.color} soft={rc.soft} data-id="element-1056" data-mp="2t59tcyjvcast" />
                      
                      </td>
                      <td style={{
                  padding: '13px 16px',
                  color: T.textSecondary,
                  maxWidth: 260
                }} data-id="element-1057" data-mp="1hnichc2ccyi0">
                      
                        {sucNames}
                      </td>
                      <td style={{
                  padding: '13px 16px'
                }} data-id="element-1058" data-mp="1bac3yysfx2u1">
                      
                        <div style={{
                    display: 'flex',
                    gap: 6
                  }} data-id="element-1059" data-mp="12fxpuzjnbyci">
                        
                          <button className="getr-btn-ghost" onClick={() => onEdit(u.id)} style={ghostButton} data-id="element-1060" data-mp="16fq1jl4c6km3">
                          
                            Editar
                          </button>
                          {u.rol !== 'admin' && <button onClick={() => setConfirm(u.id)} style={{
                      ...ghostButton,
                      color: u.archivado ? T.info : T.warn,
                      borderColor: u.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
                      background: u.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
                    }} data-id="element-1061" data-mp="xe8e1holngnd">
                          
                              {u.archivado ? 'Desarchivar' : 'Archivar'}
                            </button>}
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>
          <div style={{
        padding: '4px 16px 14px'
      }} data-id="element-1062" data-mp="g076aieosr2h">
          
            <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="usuarios" data-id="element-1063" data-mp="b7e0rsbe9epc" />
          
          </div>
        </div>}

      {confirm && <Modal title="Confirmar acción" onClose={() => setConfirm(null)} data-id="element-1064" data-mp="kx7f312x87xa">
          <div style={{
        color: T.textSecondary,
        marginBottom: 24,
        fontSize: 14,
        lineHeight: 1.5
      }} data-id="element-1065" data-mp="yy0bjrcs9d31">
          
            ¿Estás seguro de que deseas{' '}
            {state.usuarios.find(u => u.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}{' '}
            a este usuario?
          </div>
          <div style={{
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end'
      }} data-id="element-1066" data-mp="2vrv0i62bg5hl">
          
            <button onClick={() => setConfirm(null)} style={{
          ...ghostButton,
          padding: '10px 20px'
        }} data-id="element-1067" data-mp="14lyv523h6de3">
            
              Cancelar
            </button>
            <button onClick={() => {
          onDelete(confirm);
          setConfirm(null);
        }} style={{
          ...primaryButton,
          background: state.usuarios.find(u => u.id === confirm)?.archivado ? T.info : T.warn,
          borderColor: state.usuarios.find(u => u.id === confirm)?.archivado ? T.info : T.warn,
          color: '#0B1120'
        }} data-id="element-1068" data-mp="19n60kui5b0hl">
            
              Sí,{' '}
              {state.usuarios.find(u => u.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}
            </button>
          </div>
        </Modal>}
    </PageShell>;
}